import { useContext, useEffect, useState } from "react";
import BusinessIcon from "@mui/icons-material/Business";
import {
  useEmpresasApi,
  EmpresaItem,
  EmpresaDto,
} from "../../../services/api/empresasApi";
import EmpresaDataTable from "../../../components/module/admin/GenericDataTable/EmpresaDataTable";
import EmpresaModal from "../../../components/module/admin/EmpresaModal/EmpresaModal";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import {
  Box,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material";
import { Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { DarkModeContext } from "../../../context/darkModeContext";

const Empresas = () => {
  const [items, setItems] = useState<EmpresaItem[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EmpresaItem | null>(null);
  const { isLoading, getAll, create, update, remove } = useEmpresasApi();
  const { darkMode } = useContext(DarkModeContext);
  
  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 90 },
    { field: "Nombre", headerName: "Nombre", flex: 1, minWidth: 200 },
    { field: "NIT", headerName: "NIT", flex: 1, minWidth: 150 },
    { field: "RepresentanteLegal", headerName: "Representante Legal", flex: 1, minWidth: 200 },
    {
      field: "Activo",
      headerName: "Estado",
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Switch
          checked={params.row.Activo}
          onChange={(e) => handleStatusChange(params.row.Id, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton onClick={() => handleEdit(params.row)} color="primary" size="small">
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton onClick={() => handleDelete(params.row.Id)} color="error" size="small">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const loadItems = async () => {
    const data = await getAll();
    if (Array.isArray(data)) {
      setItems(data);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: EmpresaItem) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: "Esta acción eliminará la empresa y podría afectar las sucursales asociadas.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const success = await remove(id);
        if (success) {
          await loadItems();
          toast.success("La empresa ha sido eliminada.");
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        toast.error("No se pudo eliminar el registro.");
      }
    }
  };

  const handleStatusChange = async (id: number, active: boolean) => {
    const result = await Swal.fire({
      title: "¿Está seguro?",
      text: `¿Desea ${active ? "activar" : "desactivar"} esta empresa?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Sí, ${active ? "activar" : "desactivar"}`,
      cancelButtonText: "Cancelar",
      confirmButtonColor: '#3498db',
      cancelButtonColor: '#e74c3c',
    });

    if (result.isConfirmed) {
      try {
        const item = items.find((item) => item.Id === id);
        if (item) {
          const updateData: EmpresaDto = {
            Nombre: item.Nombre,
            NIT: item.NIT,
            Direccion: item.Direccion,
            RepresentanteLegal: item.RepresentanteLegal,
            Logo: item.Logo,
            Activo: active,
            Eliminado: false,
          };
          await update(id, updateData);
          await loadItems();
          toast.success("El estado de la empresa ha sido actualizado.");
        }
      } catch (err) {
        console.error("Error updating status:", err);
        const error = err as Error;
        Swal.fire("Error", error.message || "No se pudo actualizar el estado.", "error");
      }
    }
  };

  const handleModalSubmit = async (data: EmpresaDto) => {
    try {
      if (editingItem) {
        await update(editingItem.Id, data);
      } else {
        await create(data);
      }
      setIsModalOpen(false);
      await loadItems();
      toast.success("Registro guardado correctamente");
    } catch (error: any) {
      throw error;
    }
  };

  return (
    <PageLayout icon={BusinessIcon} title="Empresas">
        <div style={{ padding: "24px" }}>
          <EmpresaDataTable
            items={items}
            columns={columns}            
            onEdit={handleEdit}
            onDelete={handleDelete}
            onStatusChange={handleStatusChange}
            onAdd={handleAdd}
            isLoading={isLoading}
          />
          <EmpresaModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onSubmit={handleModalSubmit}
            title={editingItem ? "Editar Empresa" : "Nueva Empresa"}
            initialData={
              editingItem
                ? {
                    Nombre: editingItem.Nombre,
                    NIT: editingItem.NIT,
                    Direccion: editingItem.Direccion,
                    RepresentanteLegal: editingItem.RepresentanteLegal,
                    Logo: editingItem.Logo,
                    Activo: editingItem.Activo,
                    Eliminado: editingItem.Eliminado,
                  }
                : undefined
            }
            isLoading={isLoading}
            isEdit={!!editingItem}
          />
        </div>
      </PageLayout>
  );
};

export default Empresas;